import { BaseGridItem, DesktopGridItem } from 'shared/types/desktop-grid'

import {
  checkSlotFits,
  createGrid,
  markSlotOccupied
} from './gridLayoutHelpers'

/**
 * Places items sequentially in a grid, respecting existing occupied positions.
 * Returns the items with updated positions.
 *
 * @param itemsToPlace - Items that need to be placed (positions will be calculated)
 * @param existingItems - Items already on the grid (their positions are preserved)
 * @param cols - Number of columns in the grid
 * @param rows - Number of rows in the grid
 */
export const placeItemsInGridSequentially = (
  itemsToPlace: DesktopGridItem[],
  existingItems: BaseGridItem[],
  cols: number,
  rows: number
): DesktopGridItem[] => {
  if (!itemsToPlace.length || cols <= 0 || rows <= 0) return itemsToPlace

  // Compute enough rows for existing items + worst-case stack of all new items
  const existingMaxRow = existingItems.reduce(
    (max, item) => Math.max(max, item.position.row + item.size.height),
    rows
  )
  const extraRows = itemsToPlace.reduce(
    (sum, item) => sum + item.size.height,
    0
  )
  const gridRows = existingMaxRow + extraRows

  const grid = createGrid(gridRows, cols)
  existingItems.forEach(item => {
    // Synthetic group-boundary obstacles may intentionally start at row -1
    // (the title row above a group at row 0). Clip all persisted/derived
    // obstacles before indexing the grid so synchronization cannot crash.
    const startRow = Math.max(0, item.position.row)
    const endRow = Math.min(gridRows, item.position.row + item.size.height)
    const startCol = Math.max(0, item.position.col)
    const endCol = Math.min(cols, item.position.col + item.size.width)

    if (startRow >= endRow || startCol >= endCol) return

    markSlotOccupied(
      grid,
      startRow,
      startCol,
      endCol - startCol,
      endRow - startRow
    )
  })

  // Place each new item in the first available slot, growing beyond visible rows if needed
  const placedItems: DesktopGridItem[] = []

  itemsToPlace.forEach(item => {
    let placed = false

    for (let row = 0; row < gridRows && !placed; row++) {
      for (let col = 0; col < cols && !placed; col++) {
        const fits = checkSlotFits(
          grid,
          row,
          col,
          item.size.width,
          item.size.height,
          gridRows,
          cols
        )

        if (fits) {
          markSlotOccupied(grid, row, col, item.size.width, item.size.height)
          placedItems.push({
            ...item,
            position: { row, col }
          })
          placed = true
        }
      }
    }

    if (!placed) {
      placedItems.push(item)
    }
  })

  return placedItems
}
